import { useQuery } from '@tanstack/react-query';
import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { api, ProductionLayout } from '@/api/backend';
import { NotFound } from '@/pages/Errors';
import { cutlistsKeys } from '@/queries/keys';
import { Cut, CutResult, deriveCuts, Rect } from '@/utils/cuts';

import styles from './Production.module.css';

const mm = (value: number) => Number(value.toFixed(1));
const dims = ({ lengthMM, widthMM }: Pick<Rect, 'lengthMM' | 'widthMM'>) =>
  `${mm(lengthMM)} × ${mm(widthMM)}`;

export const Production = () => {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const { data, isPending, isError } = useQuery({
    queryKey: cutlistsKeys.production(id),
    queryFn: () => api.getProductionPlan(id),
  });
  const [layoutIndex, setLayoutIndex] = React.useState(0);

  if (isPending) return <div className={styles.page} />;
  if (isError) return <NotFound />;

  const layouts = data.groups.flatMap((group) => group.layouts);
  const layout = layouts[layoutIndex];
  const hasOptimalonCuts = layouts.some((sheet) => sheet.cuts.length);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>{t('production.title', { id: data.shortId })}</h1>
        {data.title && <span>{data.title}</span>}
      </header>

      {!layout || !hasOptimalonCuts ? (
        <p className={styles.empty}>{t('production.noCuttingPlan')}</p>
      ) : (
        <>
          <nav className={styles.sheets}>
            {layouts.map((sheet, index) => (
              <button
                key={index}
                className={cn(styles.sheetTab, {
                  [styles.active]: index === layoutIndex,
                })}
                onClick={() => setLayoutIndex(index)}
              >
                <strong>
                  {index + 1}. {sheet.articleCode}
                </strong>
                <span>
                  {dims(sheet)} ·{' '}
                  {t('production.sheetCount', { count: sheet.count })}
                </span>
              </button>
            ))}
          </nav>
          {layout.cuts.length ? (
            <LayoutView
              key={layoutIndex}
              layout={layout}
              kerfMM={data.kerfMM ?? 0}
            />
          ) : (
            <p className={styles.empty}>{t('production.noCuttingPlan')}</p>
          )}
        </>
      )}
    </div>
  );
};

const LayoutView = ({
  layout,
  kerfMM,
}: {
  layout: ProductionLayout;
  kerfMM: number;
}) => {
  const { t } = useTranslation();
  const cuts = React.useMemo(
    () =>
      deriveCuts({
        sheet: {
          x: 0,
          y: 0,
          lengthMM: layout.lengthMM,
          widthMM: layout.widthMM,
        },
        parts: layout.placements,
        lines: layout.cuts,
        kerfMM,
      }),
    [layout, kerfMM]
  );
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const active = activeIndex === null ? null : cuts[activeIndex];

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const step = ({ ArrowDown: 1, ArrowUp: -1 } as Record<string, number>)[
        event.key
      ];
      if (!step) return;
      event.preventDefault();
      setActiveIndex((index) =>
        Math.min(cuts.length - 1, Math.max(0, (index ?? -1) + step))
      );
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cuts.length]);

  return (
    <div className={styles.layout}>
      <section className={styles.diagram}>
        <dl className={styles.facts}>
          <dt>{t('production.material')}</dt>
          <dd>
            {[layout.articleCode, layout.materialDescription]
              .filter(Boolean)
              .join(' – ')}
          </dd>
          <dt>{t('production.sheets')}</dt>
          <dd>
            <strong>{layout.count}</strong>
          </dd>
          {kerfMM > 0 && (
            <>
              <dt>{t('production.kerf')}</dt>
              <dd>{t('production.millimeters', { value: kerfMM })}</dd>
            </>
          )}
        </dl>
        <SheetDiagram layout={layout} active={active} />
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('production.ref')}</th>
              <th>{t('production.label')}</th>
              <th>{t('production.dimensions')}</th>
              <th>{t('production.quantity')}</th>
            </tr>
          </thead>
          <tbody>
            {layout.parts.map((part) => (
              <tr key={part.ref}>
                <td>
                  <strong>{part.ref}</strong>
                </td>
                <td>{part.label}</td>
                <td>{dims(part)}</td>
                <td>{part.quantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <aside className={styles.cuts}>
        <h2>{t('production.cuts', { count: cuts.length })}</h2>
        <table className={cn(styles.table, styles.cutsTable)}>
          <thead>
            <tr>
              <th>#</th>
              <th>{t('production.piece')}</th>
              <th>{t('production.cut')}</th>
              <th>{t('production.result')}</th>
            </tr>
          </thead>
          <tbody>
            {cuts.map((cut, index) => (
              <tr
                key={index}
                className={cn({ [styles.active]: index === activeIndex })}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => setActiveIndex(index)}
              >
                <td>{index + 1}</td>
                <td>{dims(cut.piece)}</td>
                <td className={styles.cutPosition}>
                  {cut.axis}={mm(cut.atMM)}
                </td>
                <td>
                  <Results results={cut.results} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </aside>
    </div>
  );
};

const Results = ({ results }: { results: CutResult[] }) => {
  const { t } = useTranslation();
  if (!results.length) return <span className={styles.muted}>–</span>;

  return (
    <>
      {results.map((result, index) =>
        result.kind === 'part' ? (
          <strong key={index} className={styles.result}>
            {result.ref}
          </strong>
        ) : (
          <span key={index} className={cn(styles.result, styles.muted)}>
            {t('production.offcut', { dimensions: dims(result.rect) })}
          </span>
        )
      )}
    </>
  );
};

const SheetDiagram = ({
  layout,
  active,
}: {
  layout: ProductionLayout;
  active: Cut | null;
}) => {
  const strokeWidth = Math.max(layout.lengthMM, layout.widthMM) / 400;
  const isInActivePiece = (rect: Rect) =>
    !active ||
    (rect.x >= active.piece.x &&
      rect.y >= active.piece.y &&
      rect.x + rect.lengthMM <= active.piece.x + active.piece.lengthMM &&
      rect.y + rect.widthMM <= active.piece.y + active.piece.widthMM);

  return (
    <svg
      className={styles.svg}
      viewBox={`0 0 ${layout.lengthMM} ${layout.widthMM}`}
      preserveAspectRatio="xMidYMid meet"
    >
      <rect
        className={styles.sheet}
        width={layout.lengthMM}
        height={layout.widthMM}
        strokeWidth={strokeWidth}
      />
      {layout.placements.map((part, index) => (
        <g
          key={index}
          className={cn(styles.part, {
            [styles.faded]: !isInActivePiece(part),
          })}
        >
          <rect
            x={part.x}
            y={part.y}
            width={part.lengthMM}
            height={part.widthMM}
            strokeWidth={strokeWidth}
          />
          <text
            x={part.x + part.lengthMM / 2}
            y={part.y + part.widthMM / 2}
            fontSize={part.fontSize}
          >
            <tspan
              x={part.x + part.lengthMM / 2}
              dy={part.dimensions ? -part.fontSize * 0.6 : 0}
              fontWeight="bold"
            >
              {part.ref}
            </tspan>
            {part.dimensions && (
              <tspan x={part.x + part.lengthMM / 2} dy={part.fontSize * 1.2}>
                {part.dimensions}
              </tspan>
            )}
          </text>
        </g>
      ))}
      {active && (
        <>
          <rect
            className={styles.piece}
            x={active.piece.x}
            y={active.piece.y}
            width={active.piece.lengthMM}
            height={active.piece.widthMM}
            strokeWidth={strokeWidth * 3}
          />
          <line
            className={styles.cutLine}
            {...cutLine(active)}
            strokeWidth={strokeWidth * 4}
          />
        </>
      )}
    </svg>
  );
};

const cutLine = ({ piece, axis, atMM }: Cut) =>
  axis === 'x'
    ? {
        x1: piece.x + atMM,
        x2: piece.x + atMM,
        y1: piece.y,
        y2: piece.y + piece.widthMM,
      }
    : {
        x1: piece.x,
        x2: piece.x + piece.lengthMM,
        y1: piece.y + atMM,
        y2: piece.y + atMM,
      };
